// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { useEffect, useRef, useState } from 'react';



// ----------------------------------------------------------------------------
//   Setup
// ----------------------------------------------------------------------------


/**
 * Enum-type object for video manager events.
 * Distinguishes manual play/pause events from internal handling.
 */
export const VideoManagerEvents = Object.freeze({
  MANUAL_PLAY:  'MANUAL_PLAY',
  MANUAL_PAUSE: 'MANUAL_PAUSE',
});



// ----------------------------------------------------------------------------
//   React Hook
// ----------------------------------------------------------------------------

/**
 * Manage the playback state of a video element.
 */
export function useVideoManager( ref, config = {} ) {

  // --------------------------------------------------------------------------
  //   Config
  // --------------------------------------------------------------------------

  // Unpack the config object
  const {
    eventLogger = (videoManagerEvent, eventMetadata) => {},
    verbose     = false,
  } = config;


  // --------------------------------------------------------------------------
  //   Context & State
  // --------------------------------------------------------------------------

  // Create a reference for the state, so consumers always have the most up-to-date
  // versions of the state variables
  const videoStateRef = useRef({
    setVideoRef:        () => {},
    videoRef:           null,
    isPlaying:          false,
    isPaused:           false,
    isBuffering:        false,
    isOnHold:           false,
    isCompleted:        false,
    progressSeconds:    0,
    progressPercent:    0.0,
    maxProgressSeconds: 0,
    maxProgressPercent: 0.0,
  });

  // Create an internal reference for the video object
  const videoRef = useRef(null);

  // Track when the video element is attached using state (triggers effects)
  const [videoElement, setVideoElement] = useState(null);

  // Callback ref that gets called when the video element attaches
  const setVideoRef = (element) => {
    videoRef.current = element;
    setVideoElement(element); // This triggers effects
    if (ref) {
      ref.current = element; // Also update the passed ref if provided
    }
  };

  // Control video playback
  // By using a variable, we can separate the "should play" logic from the actual
  // play() call, letting us e.g. wait until a video is loaded
  const [ shouldBePaused, setShouldBePaused ] = useState( videoRef?.current?.paused ?? true );

  // Store a ref to the pause variable, so callbacks can reference the current value
  // when invoked
  const shouldBePausedRef = useRef();
  shouldBePausedRef.current = shouldBePaused;

  // Track whether the video is currently loading data
  const [ isBuffering, setIsBuffering ] = useState(false);

  // Place a hold on the video playback, while e.g. seeking
  const [ isOnHold, setIsOnHold ] = useState(false);

  // Track video progress as a percentage
  // The max progress value is only updated by watching the video at normal speed
  const [ progressPercent,    setProgressPercent    ] = useState(0);
  const [ maxProgressSeconds, setMaxProgressSeconds ] = useState(0);

  // Compute the progress & maxProgress in the other units
  const progressSeconds    = videoRef?.current?.currentTime ?? 0
  const maxProgressPercent = videoRef?.current?.duration ? (maxProgressSeconds / videoRef.current.duration * 100) : 0

  // Track whether the video has been completed
  const [ isCompleted, setIsCompleted ] = useState(false);


  // --------------------------------------------------------------------------
  //   Callbacks
  // --------------------------------------------------------------------------

  /**
   * Raise an internal event to the parent to be logged, if applicable.
   */
  const logEvent = ( videoManagerEvent, eventMetadata = undefined ) => {
    if (eventLogger !== null && eventLogger !== undefined) {

      // Compute progress values fresh from video element
      const progressSeconds = videoRef.current?.currentTime
      const progressPercent = videoRef.current?.duration ? (videoRef.current?.currentTime / videoRef.current?.duration * 100) : undefined

      // Log the event, along with some standard metadata
      eventLogger(
        videoManagerEvent,
        {
          ...(eventMetadata || {}),
          progressSeconds,
          progressPercent,
          'shouldBePaused':  shouldBePausedRef.current,
        }
      );
    }
  }

  /**
   * Update the buffering state variable to reflect the video element's ready state.
   */
  const updateBufferingState = () => {
    const readyState = videoRef?.current?.readyState;
    const newVal = readyState === undefined || readyState < 4;
    if (verbose) console.debug(`Updating buffering state: ${videoRef?.current?.readyState} => isBuffering := ${newVal}`)
    setIsBuffering( newVal );
  }

  /**
   * Handle the `timeupdate` event from the video element by updating the
   * progress values.
   */
  const timeupdateHandler = () => {

    // Check for valid video element target
    if (!videoRef.current) return;
    const $videoElement = videoRef.current;

    // Update the progress values
    setProgressPercent( $videoElement.currentTime / $videoElement.duration * 100 );
    setMaxProgressSeconds((maxProgressSeconds) => Math.max( $videoElement.currentTime, maxProgressSeconds ))

    // Update the buffering state
    updateBufferingState();
  }

  /**
   * Handle `ended` event from the video element when the end of the media
   * is reached.
   */
  const endedHandler = () => {

    // Check for valid video element target
    if (!videoRef.current) return;

    // Set the pause & completion flags
    setShouldBePaused(true);
    setIsCompleted(true);
  }

  /**
   * Add an event listener to the video element.
   * Intercepts some event types.
   */
  const addVideoEventListener = ( eventName, listener, options ) => {

    // Make sure video target is legitimate
    if (!videoRef.current) return;
    const $videoElement = videoRef.current;

    // Intercept the pause event, and only propagate it to the registered listener
    // if the video is conceptually paused -- not just on hold for a user interaction
    if (eventName === "pause") {
      $videoElement.addEventListener(
        eventName, () => {
          if (shouldBePausedRef.current) {
            listener();
          }
          else {
            console.debug('Video `pause` callback blocked -- video is not actually paused.');
          }
        }, options
      );
    }

    // Attach the other event listeners as-is
    else {
      $videoElement.addEventListener( eventName, listener, options );
    }
  }


  // --------------------------------------------------------------------------
  //   Effects
  // --------------------------------------------------------------------------

  // A set of "core" or "internal" event listeners, defined by this class itself
  // Kept separate from "external" event listeners so they avoid overwriting each other
  const coreEventListeners = {

    // Update handlers
    "timeupdate":  [ timeupdateHandler, undefined ],
    "ended":       [ endedHandler,      undefined ],

    // Extra checks for buffering state, in case it updates quicker than useEffect can track
    // There doesn't appear to be any single readyState change hook,
    // but this is the set of callbacks associated with readyState changes
    "loadedmetadata": [ updateBufferingState, undefined ],
    "loadeddata":     [ updateBufferingState, undefined ],
    "canplay":        [ updateBufferingState, undefined ],
    "canplaythrough": [ updateBufferingState, undefined ],
  }

  // Track "external" event listeners for the video element, set by the consumer
  // This lets us detach/reattach them if the reference changes
  const [ eventListeners, setEventListeners ] = useState({});

  /**
   * Match the buffering state variable to the video element's ready state.
   */
  useEffect(() => {
    if (verbose) console.debug(`Video readyState changed: ${videoRef?.current?.readyState}`)
    updateBufferingState();
  }, [ videoRef?.current?.readyState ])

  /**
   * Add listeners to the video element to keep state values in sync during playback.
   */
  useEffect(() => {
    if (!videoRef.current) return;
    if (verbose) console.debug(`Adding listeners...`);

    // Save current video ref, so callbacks can reference the same one even after it changes
    const $videoElement = videoRef.current;

    // Check buffering state immediately when ref is attached
    updateBufferingState();

    // Add "external" event listeners (defined by the consumer)
    (Object.keys(eventListeners) || []).forEach((eventName) => {
      addVideoEventListener( eventName, ...eventListeners[eventName] );
    });

    // Add "internal" event listeners (defined by this class itself)
    (Object.keys(coreEventListeners) || []).forEach((eventName) => {
      addVideoEventListener( eventName, ...coreEventListeners[eventName] );
    });

    // Remove all event listeners ( both"internal" and "external")
    // if / when the referenced video element changes
    return () => {
      (Object.keys(eventListeners) || []).forEach((eventName) => {
        $videoElement.removeEventListener( eventName, ...eventListeners[eventName] );
      });
      (Object.keys(coreEventListeners) || []).forEach((eventName) => {
        $videoElement.removeEventListener( eventName, ...coreEventListeners[eventName] );
      });
    }

  // Invoke on changes to the video element ~or~ the list of registered event listeners
  }, [ videoElement, eventListeners ])

  /**
   * When internal state variables change, check if video needs to start playing
   * and update it accordingly.
   */
  useEffect(() => {
    if (!videoRef.current) return;
    if (verbose) console.debug(`Updating play/pause state... onHold = ${isOnHold}, buffering = ${isBuffering}, shouldBePaused = ${shouldBePaused}`);

    // If there should be a hold on the video playback, make sure it's paused
    if ((isOnHold || isBuffering || shouldBePaused) && !videoRef.current.paused) {
      if (verbose) console.debug(`Verdict: pause`);
      videoRef.current.pause();
    }

    // If there aren't any holds on the video playback (user interaction,
    // buffering, etc), play the video element
    if (!(isOnHold || isBuffering || shouldBePaused) && videoRef.current.paused) {

      // If there's still video content left, play as normal
      if (progressPercent < 100) {
        if (verbose) console.debug(`Verdict: play`);
        videoRef.current.play();
      }

      // If the video is at the end, trigger all the completion logic instead
      else {
        if (verbose) console.debug(`Verdict: video ended`);
        setShouldBePaused(true);
        setIsCompleted(true);
        eventListeners.ended?.[0]?.();
      }
    }

    // Check if we're paused at the end of the video, and trigger the completion logic if so
    if (videoRef.current.paused && progressPercent >= 100) {
      if (verbose) console.debug(`Verdict: video ended`);
      setShouldBePaused(true);
      setIsCompleted(true);
      eventListeners.ended?.[0]?.();
    }

  }, [ isOnHold, isBuffering, shouldBePaused ])


  // --------------------------------------------------------------------------
  //   Return Objects
  // --------------------------------------------------------------------------

  // Update the current state ref
  // Handle this way so consumers can always access the most up-to-date versions
  // of all the state variables, even in e.g. callbacks
  videoStateRef.current = {

      /**
       * A callback ref to attach to the video element.
       * To use the ref elsewhere, use `videoRef` instead.
       */
      setVideoRef,

      /**
       * The ref object containing the video element.
       *
       * **IMPORTANT: To set this ref on an element, use `setVideoRef` instead!**
       */
      videoRef,

      /**
       * Whether or not the video is actively playing.
       * Aggregates the other playback variables into a single boolean.
       */
      isPlaying: !(shouldBePaused || isBuffering || isOnHold || videoRef?.current?.paused),

      /** Whether or not the video is currently paused. */
      isPaused: videoRef?.current?.paused || shouldBePaused,

      /** Whether or not the video is currently loading data. */
      isBuffering,

      /**
       * Whether or not the video is currently being held back from playing,
       * e.g. due to an ongoing user interaction.
       */
      isOnHold,

      /**
       * Whether or not video playback has been completed.
       *
       * Set to `true` once the end is reached, and remains `true` even if
       * a user rewinds or replays sections of the video.
       */
      isCompleted,

      /** The current progress into the video, in number of seconds. */
      progressSeconds,

      /** The current progress into the video, as a percentage of the total duration. */
      progressPercent,

      /** The maximum progress that has been made into the video, in number of seconds. */
      maxProgressSeconds,

      /** The maximum progress that has been made into the video, as a percentage of the total duration. */
      maxProgressPercent,
  };

  // Return the state, plus a set of callbacks
  return {

      /**
       * The state object.
       */
      state: videoStateRef,

      /** Start playing the video */
      play: () => {
        if (verbose) console.debug(`Video manager \`play\` invoked.`);
        logEvent( VideoManagerEvents.MANUAL_PLAY );
        setShouldBePaused(false);
      },

      pause: () => {
        if (verbose) console.debug(`Video manager \`pause\` invoked.`);
        logEvent( VideoManagerEvents.MANUAL_PAUSE );
        setShouldBePaused(true);
      },

      /**
       * Seek the video player to the given percentage value.
       */
      seekToPercent: (percent, options) => {

        // Make sure valid video target exists
        if (!videoRef?.current) return;

        // Unpack the options object
        const {
          ignoreMax = false,
          holdUntilFinished = true,
        } = options;

        // Unless ignoring the maximum progress, use it as a cap
        if (!ignoreMax) {
          if (percent > maxProgressPercent) percent = maxProgressPercent;
        }

        // If desired, pause the video while seeking
        if ( holdUntilFinished ) {
          setIsOnHold(true);
        }

        // Update video & state values
        videoRef.current.currentTime = percent * videoRef.current.duration / 100;
        setProgressPercent( percent );
      },

      /**
       * Move the current video time by the given number of seconds.
       */
      moveBySeconds: (seconds, ignoreMax = false) => {

        // Make sure valid video target exists
        if (!videoRef?.current) return;

        // Compute the new intended values
        let newSeconds = videoRef.current.currentTime + seconds;

        // Unless ignoring the maximum progress, use it as a cap
        if (!ignoreMax) {
          if (newSeconds > maxProgressSeconds) newSeconds = maxProgressSeconds;
        }

        // Update the video & state values
        videoRef.current.currentTime = newSeconds;
        setProgressPercent( videoRef.current.currentTime / videoRef.current.duration * 100 );
      },

      /**
       * Finish the seeking action.
       */
      finishSeeking: () => {
        setIsOnHold(false);
      },

      /**
       * Register a callback function to be run on certain video playback events.
       */
      setEventListener: ( eventName, listener, options = undefined ) => {

        // Add the new event listener to the set, so it can be (re)applied if the ref changes
        setEventListeners(
          (listeners) => ({ ...listeners, [eventName]: [listener, options] })
        );
      }
  }
}
